import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import TextButton from 'LuxKit/Button/TextButton'
import { pluralizeToString } from 'lib/string/pluralize'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useBedbankDiscountFromPriceAndValue } from 'hooks/useBedbankDiscount'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getSource } from 'selectors/offerSelectors'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import { SOURCE_TYPES } from 'constants/source'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import { arrayToObject } from 'lib/array/arrayUtils'
import { RoomWithData } from 'hooks/OfferPage/useBedbankBuyCallbacks'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useRoomOptionRateBedbankQuote from 'hooks/Insurance/useRoomOptionRateBedbankQuote'
import BedbankRoomOptionRateInsurance from '../BedbankRoomOptionRateInsurance'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'

const FlexAutoGroup = styled(Group)`
  & > * {
    flex: auto;
  }
`

const LoyaltyPointsWrapper = styled.div`
  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  rooms: Array<OfferPageRoom>
  roomsWithData: Array<RoomWithData>
  offer: App.BedbankOffer | App.BedbankOfferSummary
  flightOrigin?: App.Airport
  flightPrice?: number
  checkIn?: string
  departureTooEarly?: boolean
  flightsEnabled?: boolean
  luxPlusLPPEnabled: boolean
  userMultiItemCartEnabled: boolean
  isFlightsSelected: boolean
  isLoggedIn: boolean
  canProceed: boolean
  onBookNow: (keepBrowsing?: boolean, isInsuranceSelected?: boolean, roomsWithData?: Array<RoomWithData>) => void
  onAddToUserCartNow: (roomsWithData?: Array<RoomWithData>, isInsuranceSelected?: boolean) => void
}

function BedbankMultiRoomPricing(props: Props) {
  const {
    rooms,
    roomsWithData,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    onBookNow,
    onAddToUserCartNow,
    luxPlusLPPEnabled,
    userMultiItemCartEnabled,
    isFlightsSelected,
    isLoggedIn,
    canProceed,
  } = props
  const { currentRegionCode } = useContext(GeoContext)

  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice
  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return moment(checkIn).diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn

  const {
    propertyFees,
    bundlePropertyFees,
    hotelPrice,
    hotelMemberPrice,
    bundlePrice,
    bundleMemberPrice,
    hotelValue,
    bundleValue,
  } = useMemo(() => {
    return roomsWithData.reduce((acc, roomData) => {
      const rate = roomData.rate
      const bundleRate = roomData.bundleRate
      const { propertyFees = 0, taxesAndFees } = rate.totals

      return {
        propertyFees: acc.propertyFees + propertyFees,
        taxesAndFees: acc.taxesAndFees + taxesAndFees,
        bundlePropertyFees: acc.bundlePropertyFees + (bundleRate?.totals.propertyFees || propertyFees),
        bundleTaxesAndFees: acc.bundleTaxesAndFees + (bundleRate?.totals.taxesAndFees || taxesAndFees),
        hotelPrice: acc.hotelPrice + rate.totals.inclusive,
        hotelMemberPrice: acc.hotelMemberPrice + (rate.totals.memberInclusive || rate.totals.inclusive),
        bundlePrice: acc.bundlePrice + (bundleRate?.totals.inclusive || rate.totals.inclusive),
        bundleMemberPrice: acc.bundleMemberPrice + (bundleRate?.totals.memberInclusive || bundleRate?.totals.inclusive || rate.totals.memberInclusive || rate.totals.inclusive),
        margin: acc.margin + (rate.margin || 0),
        marginAud: acc.marginAud + (rate.marginAud || 0),
        bundleMargin: acc.bundleMargin + (bundleRate?.margin || 0),
        bundleMarginAud: acc.bundleMarginAud + (bundleRate?.marginAud || 0),
        hotelValue: acc.hotelValue + rate.value,
        bundleValue: acc.bundleValue + (bundleRate?.value || 0),
      }
    }, {
      propertyFees: 0,
      taxesAndFees: 0,
      bundlePropertyFees: 0,
      bundleTaxesAndFees: 0,
      hotelPrice: 0,
      hotelMemberPrice: 0,
      bundlePrice: 0,
      bundleMemberPrice: 0,
      margin: 0,
      marginAud: 0,
      bundleMargin: 0,
      bundleMarginAud: 0,
      hotelValue: 0,
      bundleValue: 0,
    })
  }, [roomsWithData])

  const totalsByRoom = useMemo(() => arrayToObject(
    roomsWithData,
    roomData => roomData.room.id,
    roomData => {
      const rate = roomData.rate
      const bundleRate = roomData.bundleRate
      const { propertyFees = 0 } = rate.totals
      const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees

      const hotelPrice = rate.totals.inclusive
      const hotelMemberPrice = rate.totals.memberInclusive ?? 0
      const bundlePrice = bundleRate?.totals.inclusive || hotelPrice

      return getDisplayTotalPrice({
        hotelPrice: hotelPrice + propertyFees,
        hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
        hotelValue: rate.value,
        bundlePrice: bundlePrice + bundlePropertyFees,
        bundleValue: bundleRate?.value,
        checkIn,
        flightPrice: flightPrice ? flightPrice / roomsWithData.length : undefined,
        rooms: [roomData.room],
      })
    },
  ), [checkIn, flightPrice, roomsWithData])

  const hasPromoRate = roomsWithData.some(roomData => !!roomData.promotion)

  const canAddToUserCart = hasDates && userMultiItemCartEnabled

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'total'

  const discount = useBedbankDiscountFromPriceAndValue(totalPrices.totals.value, totalPrices.totals.price, totalPrices.totals.memberPrice)
  const source = useAppSelector(state => getSource(state))

  const canShowFlightBundlePrice = flightsEnabled && hasFlights && !departureTooEarly

  const nights = roomsWithData[0].effectiveRate.nights

  // Get CFMR quote for flex packages
  const checkout = useMemo(() => {
    if (!checkIn || !nights) return undefined
    return moment(checkIn).add(nights, 'days').format(ISO_DATE_FORMAT)
  }, [checkIn, nights])
  const canTriggerFlexCfmrBedbankPackagesExperiment = useMemo(() => {
    if (!roomsWithData || !checkIn || !checkout) return false
    const effectiveRates = roomsWithData.map(roomWithData => roomWithData.effectiveRate)
    const allRoomsAreNonRefundable = effectiveRates.every(rate => !rate.partiallyRefundable && !rate.refundable)
    return allRoomsAreNonRefundable && !!checkIn && !!checkout
  }, [checkIn, checkout, roomsWithData])
  const isFlexCfmrBedbankPackagesEnabled = useOptimizelyExperiment(OptimizelyExperiments.croFlexCfmrBedbankPackages, canTriggerFlexCfmrBedbankPackagesExperiment)
  const { quote, isFetching: isQuoteFetching, error: quoteError, onSelectInsurance, isInsuranceSelected } = useRoomOptionRateBedbankQuote({
    country: offer.property.address.countryName,
    coverAmount: totalPrices.totals.price,
    checkIn,
    checkout,
    travellers: rooms,
    useCache: true,
    disabled: !isFlexCfmrBedbankPackagesEnabled,
  })

  const onBookNowHandler = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const keepBrowsing: boolean = !!event.currentTarget.dataset.keepBrowsing
    onBookNow(keepBrowsing, isInsuranceSelected)
  }, [onBookNow, isInsuranceSelected])

  const onAddToUserCartHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    onAddToUserCartNow(undefined, isInsuranceSelected)
  }, [onAddToUserCartNow, isInsuranceSelected])

  const isMetaSearch = useMemo(() => {
    return source?.includes(SOURCE_TYPES.METASEARCH)
  }, [source])

  const onLoginForMemberSavings = useCallback(async() => {
    showModal<AccountAccessModalResult>(<AccountAccessModal />)
  }, [showModal])

  const shouldDisplayLoginForMemberSavings = !isLoggedIn && isMetaSearch

  const showHotelAndFlightsToggle = flightsEnabled

  const withFlights = flightsEnabled && isFlightsSelected && !!flightOrigin

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      ...roomsWithData.map(roomData => {
        const rate = roomData.effectiveRate
        const { propertyFees = 0 } = rate.totals
        const roomTotalPrices = totalsByRoom[roomData.room.id]

        return (
          generateLoyaltyPointsCalculatorBedbankOptions(rate, {
            price: roomTotalPrices.totals.price,
            memberPrice: roomTotalPrices.totals.memberPrice,
            propertyFees,
            propertyId: offer.property.id,
            offerId: offer.id,
            propertyCountryCode: offer.property.address.countryCode,
            currentRegionCode,
          })
        )
      }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice,
        skip: !withFlights,
      }),
    ]
  }, [
    totalsByRoom,
    roomsWithData,
    offer,
    flightPrice,
    withFlights,
    currentRegionCode,
  ])

  let hotelOnlyBookNowText = 'Book now'
  if (rooms.length > 1) {
    hotelOnlyBookNowText = `Book ${pluralizeToString(offer.saleUnit, rooms.length)}`
  }

  if (withFlights) {
    return (
      <VerticalSpacer gap={12}>
        {!!isFlexCfmrBedbankPackagesEnabled && <div>
          <BedbankRoomOptionRateInsurance
            isLoading={isQuoteFetching}
            onChange={onSelectInsurance}
            errorOnQuote={!!quoteError}
            quote={quote}
            refundTitle="Full refund for unforeseen events"
            refundDetails="(Hotel only)"
          />
        </div>}
        <AccommodationPackageTilePricingBlock
          tabletDirection="horizontal"
          tabletVerticalAlign="center"
          data-testid="flights-pricing"
          title={undefined}
          callToAction={<FlexAutoGroup direction="horizontal" wrap="wrap" gap={8} noDisplayWhenEmpty>
            {flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && <>
              {canAddToUserCart && <TextButton
                size="medium"
                kind="primary"
                data-testid="book-button"
                onClick={onAddToUserCartHandler}
                disabled={!canProceed}
              >
                Select {pluralizeToString(offer.saleUnit, rooms.length)}
              </TextButton>}
              {!canAddToUserCart && <TextButton
                size="medium"
                kind="primary"
                data-testid="book-button"
                onClick={onBookNowHandler}
                disabled={!canProceed}
              >
                {hasDates ? 'Book now' : 'Check availability'}
              </TextButton>}
            </>}
            {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <TextButton
              size="medium"
              data-filter="airport"
              kind="tertiary"
              onClick={onToggleFilter}
            >
              Change airport
            </TextButton>}
            {flightOrigin.code !== offer.flights?.airportCode && (departureTooEarly || departureTooFarInFuture) && <TextButton
              size="medium"
              data-filter="checkin"
              kind="tertiary"
              onClick={onToggleFilter}
            >
              Change departure date
            </TextButton>}
          </FlexAutoGroup>}
        >
          <VerticalSpacer gap={12}>
            {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <BodyText
              variant="small"
              colour="neutral-two"
            >
              No flights available from {flightOrigin.name}. Please select another departing airport.
            </BodyText>}
            {!hasFlights && departureTooFarInFuture && (
              <BodyText variant="small" colour="neutral-two">
                Flights can only be booked up to 11 months in advance.
              </BodyText>
            )}
            {flightOrigin.code !== offer.flights?.airportCode && departureTooEarly && <BodyText
              variant="small"
              colour="neutral-two"
            >
              Flights cannot be booked online when check-in is within 24 hours
            </BodyText>}
          </VerticalSpacer>

          {!hasFlights && flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && (
            <VerticalSpacer gap={12}>
              <div>
                <PriceRowPriceCaption>
                  {pluralizeToString('night', nights)}{rooms.length > 1 ? `, ${pluralizeToString(offer.saleUnit, rooms.length)}` : ''}{hasDates ? ' from' : ''}
                </PriceRowPriceCaption>
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <LuxPlusPriceGuard
                    showMemberPrice={(bundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                    fallbackPrice={<PriceRowPrice
                      data-testid="bedbank-package-price"
                      size="M"
                      price={bundlePrice}
                      saleUnit=""
                    />}
                  >
                    <LuxPlusPriceStack
                      size="M"
                      price={bundlePrice}
                      memberPrice={bundleMemberPrice}
                      saleUnit=""
                    />
                  </LuxPlusPriceGuard>
                  <Caption variant="large">plus flights</Caption>
                </Group>
                {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}

                {roomsWithData.map(roomData => {
                  return (
                    <PriceRowAgentHubCommission
                      key={roomData.room.id}
                      size="M"
                      productType={offer.productType}
                      offerLocations={[]}
                      offerId={offer.id}
                      packageId={roomData.effectiveRate.id}
                      offerTotal={totalsByRoom[roomData.room.id].original.flightBundlePrice}
                      kind="plain"
                    />
                  )
                })}

                {!!discount && <CSSBreakpoint max="mobile">
                  <PriceRowValueDiscountWithCaption
                    size="M"
                    originalValue={bundleValue}
                    discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
                    discountType="estimate"
                  />
                </CSSBreakpoint>}
              </div>
              <LoyaltyPointsWrapper>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={!checkIn ? 'estimate' : 'exact'}
                />
              </LoyaltyPointsWrapper>
            </VerticalSpacer>
          )}

          {canShowFlightBundlePrice && <VerticalSpacer gap={12}>
            <div>
              <PriceRowPriceCaption>
                {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', nights)}{rooms.length > 1 ? `, ${pluralizeToString(offer.saleUnit, rooms.length)}` : ''} + flights from
              </PriceRowPriceCaption>
              <LuxPlusPriceGuard
                showMemberPrice={(totalPrices.totals.flightBundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                fallbackPrice={<PriceRowPrice
                  size="M"
                  price={totalPrices.totals.flightBundlePrice}
                  saleUnit={saleUnit}
                />}
              >
                <LuxPlusPriceStack
                  size="M"
                  price={totalPrices.totals.flightBundlePrice}
                  memberPrice={totalPrices.totals.flightBundleMemberPrice}
                  saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total*'}
                />
              </LuxPlusPriceGuard>
              {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
              <PriceRowTaxesAndFees />
              {discount > 0 && !hasPromoRate && <CSSBreakpoint max="mobile">
                <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={totalPrices.totals.flightBundleValue}
                  discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
                  discountType="estimate"
                />
              </CSSBreakpoint>}
            </div>
            <LoyaltyPointsWrapper>
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType="estimate"
              />
            </LoyaltyPointsWrapper>
          </VerticalSpacer>}
          <AtolProtectedTooltip />
        </AccommodationPackageTilePricingBlock>
      </VerticalSpacer>)
  } else {
    return (
      <VerticalSpacer gap={12}>
        {!!isFlexCfmrBedbankPackagesEnabled && <div>
          <BedbankRoomOptionRateInsurance
            isLoading={isQuoteFetching}
            onChange={onSelectInsurance}
            errorOnQuote={!!quoteError}
            quote={quote}
            refundTitle="Full refund for unforeseen events"
            refundDetails="(Hotel only)"
          />
        </div>}
        <AccommodationPackageTilePricingBlock
          tabletDirection="horizontal"
          tabletVerticalAlign="center"
          data-testid="pricing"
          title={undefined}
          callToAction={<FlexAutoGroup direction="horizontal" wrap="wrap" gap={8} noDisplayWhenEmpty>
            {shouldDisplayLoginForMemberSavings && <TextButton size="large" kind="primary" nonInteractable onClick={onLoginForMemberSavings}>
              Login for member savings
            </TextButton>}
            {canAddToUserCart && (
              <TextButton
                size="medium"
                kind="secondary"
                onClick={onAddToUserCartHandler}
                startIcon={<LineShoppingCartIcon />}
                disabled={!canProceed}
              >
                Add to cart
              </TextButton>
            )}
            {!shouldDisplayLoginForMemberSavings && <TextButton
              size="medium"
              kind="primary"
              data-testid="book-button"
              onClick={onBookNowHandler}
              disabled={!canProceed}
            >
              {hasDates ? hotelOnlyBookNowText : 'Check availability'}
            </TextButton>}
          </FlexAutoGroup>}
        >
          <VerticalSpacer gap={12}>
            <div>
              <PriceRowPriceCaption>
                {pluralizeToString('night', nights)}{rooms.length > 1 ? `, ${pluralizeToString(offer.saleUnit, rooms.length)}` : ''}{hasDates ? ' from' : ''}
              </PriceRowPriceCaption>
              <LuxPlusPriceGuard
                showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                fallbackPrice={<PriceRowPrice
                  data-testid="bedbank-package-price"
                  size="M"
                  price={totalPrices.totals.price}
                  saleUnit={saleUnit}
                />}
              >
                <LuxPlusPriceStack
                  size="M"
                  price={totalPrices.totals.price}
                  memberPrice={totalPrices.totals.memberPrice}
                  saleUnit={saleUnit}
                />
              </LuxPlusPriceGuard>
              {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
              {!hasPromoRate && <PriceRowTaxesAndFees />}

              {roomsWithData.map(roomData => {
                return (
                  <PriceRowAgentHubCommission
                    key={roomData.room.id}
                    size="M"
                    productType={offer.productType}
                    offerLocations={[]}
                    offerId={offer.id}
                    packageId={roomData.effectiveRate.id}
                    offerTotal={totalsByRoom[roomData.room.id].original.price}
                    kind="plain"
                  />
                )
              })}

              {!!discount && <CSSBreakpoint max="mobile">
                <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={totalPrices.totals.value}
                  discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
                  discountType="estimate"
                />
              </CSSBreakpoint>}
            </div>
            <LoyaltyPointsWrapper>
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={!checkIn ? 'estimate' : 'exact'}
              />
            </LoyaltyPointsWrapper>
          </VerticalSpacer>
        </AccommodationPackageTilePricingBlock>
      </VerticalSpacer>)
  }
}

type MappedProps = Pick<Props,
  | 'luxPlusLPPEnabled'
  | 'userMultiItemCartEnabled'
  | 'isLoggedIn'
  | 'isFlightsSelected'
  | 'checkIn'
  | 'flightsEnabled'
  | 'flightOrigin'
  | 'offer'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isFlightsSelected: state.flights,
  checkIn: state.checkInDate,
  flightsEnabled: offerFlightsEnabled(state, appState),
  flightOrigin: state.airport,
  offer: state.bedbankOffer!,
}), BedbankMultiRoomPricing)
